import dayjs from "dayjs";
import React from "react";
import {
  ChartComponentProps,
  ChartConfig,
  createChartConfig,
} from "src/components/Referral-partner/Analytics/analyticsTypes";
import { TimeToFirstTradeDisplay } from "src/components/Referral-partner/Analytics/charts/TimeToFirstTrade";
import { WarningWrapper } from "src/components/Referral-partner/Analytics/styles/shared.styles";
import api from "src/store/features/api";
import { useRPAnalyticsState } from "src/store/features/RPAnalytics.slice";

// Define a type for our simple numeric data
export type TimeToFirstTradeData = {
  data: number;
};

export const useGetTimeToFirstTradeConfig = (): {
  timeToFirstTrade: ChartConfig<TimeToFirstTradeData>;
} => {
  const { chartDateRange } = useRPAnalyticsState();

  // Fetch data and states
  const {
    data: rawTimeToFirstTrade,
    isLoading: ttftIsLoading,
    isFetching: ttftIsFetching,
    isError: ttftIsError,
    error: ttftError,
  } = api.useGetTimeToFirstTradeQuery({
    timeFrame: chartDateRange.timeToFirstTrade,
    timezone: dayjs.tz.guess(),
  });

  // Format data as an array to match expected format
  const timeToFirstTradeData = React.useMemo((): TimeToFirstTradeData[] => {
    if (!rawTimeToFirstTrade) return [];

    const avgValue =
      typeof rawTimeToFirstTrade === "number"
        ? rawTimeToFirstTrade
        : rawTimeToFirstTrade.avgFirstTradeTime;

    return [
      {
        data: avgValue,
      },
    ];
  }, [rawTimeToFirstTrade]);

  // Create chart config
  const timeToFirstTradeConfig = React.useMemo(
    (): { timeToFirstTrade: ChartConfig<TimeToFirstTradeData> } => ({
      timeToFirstTrade: createChartConfig<TimeToFirstTradeData>({
        title: "Average Time to First Trade",
        type: "statistic",
        menuMode: "compact",
        defaultDate: "all time",
        tooltip: "",
        isLoading: ttftIsLoading || ttftIsFetching,
        data: timeToFirstTradeData,
        component: ({ data }: ChartComponentProps<TimeToFirstTradeData>): JSX.Element => {
          if (ttftIsError) {
            return <WarningWrapper>{ttftError?.message}</WarningWrapper>;
          } else if (!data || data.length === 0) {
            return <WarningWrapper color="#002035">No Data</WarningWrapper>;
          } else {
            return <TimeToFirstTradeDisplay data={data[0]} />;
          }
        },
      }),
    }),
    [timeToFirstTradeData, ttftIsLoading, ttftIsFetching, ttftIsError, ttftError],
  );

  return timeToFirstTradeConfig;
};
